import React, { useEffect, useState, useCallback } from "react";
import { Modal, Button } from "components/common";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { createPermission, updatePermission } from "redux/authorization/permissionSlice";
import apiAxios from "helpers/apiAxios";
import { FaBan, FaSave, FaMagic } from "react-icons/fa";
import { formatString } from "helpers/utils";
import Select from 'react-select';
import { PERMISSION_PARENT_GROUPS } from "app_config";
import PropTypes from 'prop-types';

import { INPUT_CLS as inputCls } from 'helpers/formStyles';
const selectStyles = {
  control: (base, state) => ({
    ...base,
    fontSize: '0.875rem',
    minHeight: '38px',
    borderRadius: '0.5rem',
    borderColor: state.isFocused ? '#60a5fa' : '#e2e8f0',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(96,165,250,0.2)' : 'none',
    '&:hover': { borderColor: '#60a5fa' },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '0.875rem',
    backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#eff6ff' : 'white',
  }),
  placeholder: base => ({ ...base, fontSize: '0.875rem', color: '#cbd5e1' }),
  menuList: (base) => ({
    ...base,
    maxHeight: '200px',
  }),
  // Render the menu above the modal (and its Save/Cancel buttons). Paired with
  // menuPortalTarget={document.body} so the menu escapes the modal's overflow
  // instead of being clipped/overlapped by it. Must exceed the Modal's own
  // zIndex (100000 in taiwincss-components/Modal) or it renders behind it.
  menuPortal: (base) => ({ ...base, zIndex: 1000001 }),
};

const Field = ({ label, required, error, hint, children }) => (
  <div className="tw-flex tw-flex-col tw-gap-1">
    <label className="tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wide">
      {label}{required && <span className="tw-text-red-400 tw-ml-0.5">*</span>}
    </label>
    {children}
    {hint && !error && <p className="tw-text-xs tw-text-slate-400 tw-mt-0.5">{hint}</p>}
    {error && <p className="tw-text-xs tw-text-red-500 tw-mt-0.5">{error}</p>}
  </div>
);

Field.propTypes = {
  label:    PropTypes.string.isRequired,
  required: PropTypes.bool,
  error:    PropTypes.string,
  hint:     PropTypes.string,
  children: PropTypes.node.isRequired,
};

const PermissionForm = ({ showModal, setshowModal, title, permission }) => {
  const { register, control, handleSubmit, formState: { errors }, setValue, reset } = useForm();
  const dispatch = useDispatch();
  const [suggesting, setSuggesting] = useState(false);

  // Fetch the next free vXXX code from the backend (avoids DB + reserved/feature-flag codes).
  const suggestCode = useCallback(async () => {
    try {
      setSuggesting(true);
      const res = await apiAxios.get("/authorization/permissions/next_code");
      if (res.data?.code) setValue("code", res.data.code, { shouldValidate: true });
    } catch (error) {
      toast.error("Could not fetch a suggested code.");
    } finally {
      setSuggesting(false);
    }
  }, [setValue]);

  // On opening the create form: reset + prefill a suggested code.
  useEffect(() => {
    if (showModal && !permission) {
      reset();
      suggestCode();
    }
  }, [showModal, permission, reset, suggestCode]);

  useEffect(() => {
    if (permission) {
      Object.entries(permission).forEach(([key, value]) => setValue(key, value));
    }
  }, [setValue, permission]);

  const closeModal = () => setshowModal(false);

  const onSubmit = async (data) => {
    try {
      if (permission) {
        dispatch(updatePermission({ permissionId: permission.id, data }));
        closeModal();
      } else {
        dispatch(createPermission(data));
        reset();
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const parentGroupOptions = PERMISSION_PARENT_GROUPS.map(o => ({ value: o, label: formatString(o) }));

  return (
    <Modal
      show={showModal}
      onHide={closeModal}
      title={title}
      size="md"
      backdropClick={false}
      footer={
        <div className="tw-flex tw-gap-2">
          <Button type="submit" form="permission-form" size="sm" variant="primary">
            <FaSave size={12} /> Save
          </Button>
          <Button variant="danger" size="sm" onClick={closeModal}>
            <FaBan size={12} /> Cancel
          </Button>
        </div>
      }
    >
      <form id="permission-form" onSubmit={handleSubmit(onSubmit)} className="tw-flex tw-flex-col tw-gap-4">

        {/* Content area — modal tự giãn theo nội dung, không cuộn */}
        <div className="tw-flex tw-flex-col tw-gap-4">
          {/* Subject class + Action — side by side */}
          <div className="tw-grid tw-grid-cols-2 tw-gap-3">
            <Field label="Subject class" required error={errors.subject_class?.message}>
              <input
                type="text"
                placeholder="e.g. Crm::Project"
                className={inputCls}
                {...register("subject_class", { required: "Subject class is required." })}
              />
            </Field>

            <Field label="Action" required error={errors.action?.message}>
              <input
                type="text"
                placeholder="e.g. index"
                className={inputCls}
                {...register("action", { required: "Action is required." })}
              />
            </Field>
          </div>

          {/* Description — full width */}
          <Field label="Description" required error={errors.description?.message}>
            <textarea
              rows={3}
              placeholder="Describe what this permission allows…"
              className={`${inputCls} tw-resize-none`}
              {...register("description", { required: "Description is required." })}
            />
          </Field>

          {/* Code + Group — side by side */}
          <div className="tw-grid tw-grid-cols-2 tw-gap-3">
            <Field label="Code" error={errors.code?.message} hint="Unique vXXX code — click Suggest to get the next free code.">
              <div className="tw-flex tw-gap-2">
                <input
                  type="text"
                  placeholder="e.g. v901"
                  className={inputCls}
                  {...register("code", {
                    // Code is optional (only menu-linked permissions need one);
                    // validate the v### format only when a value is entered.
                    validate: (v) => !v || /^v\d+$/.test(v) || "Code must be v### format (e.g. v901).",
                  })}
                />
                <button
                  type="button"
                  onClick={suggestCode}
                  disabled={suggesting}
                  title="Suggest next free code"
                  className="tw-shrink-0 tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-rounded tw-border tw-border-primary-200 tw-bg-primary-50 tw-text-primary-600 hover:tw-bg-primary-100 tw-text-xs tw-font-medium tw-cursor-pointer disabled:tw-opacity-50"
                >
                  <FaMagic size={11} /> {suggesting ? "..." : "Suggest"}
                </button>
              </div>
            </Field>

            <Field label="Group" error={errors.group_name?.message}>
              <input
                type="text"
                placeholder="e.g. Shipment"
                className={inputCls}
                {...register("group_name")}
              />
            </Field>
          </div>
        </div>

        {/* Parent group — outside scroll area */}
        <div className="tw-relative tw-z-50">
          <Field label="Parent group" required error={errors.parent_group?.message}>
            <Controller
              control={control}
              name="parent_group"
              rules={{ required: "Parent group is required." }}
              render={({ field }) => (
                <Select
                  {...field}
                  closeMenuOnSelect
                  options={parentGroupOptions}
                  value={parentGroupOptions.find(o => o.value === field.value) || null}
                  placeholder="Select parent group…"
                  classNamePrefix="react-select"
                  styles={selectStyles}
                  maxMenuHeight={200}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  menuPosition="fixed"
                  onChange={opt => setValue('parent_group', opt.value, { shouldValidate: true })}
                />
              )}
            />
          </Field>
        </div>

      </form>
    </Modal>
  );
};

PermissionForm.propTypes = {
  showModal:    PropTypes.bool.isRequired,
  setshowModal: PropTypes.func.isRequired,
  title:        PropTypes.string.isRequired,
  permission:   PropTypes.object,
};

export default PermissionForm;
